import React, { useRef, useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

const THRESHOLD = 72;

export default function PullToRefresh({ onRefresh, children, sx }) {
  const scrollRef = useRef(null);
  const startYRef = useRef(0);
  const pullRef = useRef(0);         // current pull distance (ref avoids stale closures in listeners)
  const activeRef = useRef(false);   // gesture in progress
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);

  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Keep onRefreshRef current without re-registering listeners
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (refreshingRef.current) return;
      // scrollTop can be slightly negative on iOS rubber-band
      if ((el.scrollTop ?? 0) <= 0) {
        startYRef.current = e.touches[0].clientY;
        activeRef.current = true;
      }
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || refreshingRef.current) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        activeRef.current = false;
        pullRef.current = 0;
        setPull(0);
        return;
      }
      // Dampen drag so it feels resistant
      const clamped = Math.min(delta * 0.5, THRESHOLD * 1.1);
      pullRef.current = clamped;
      setPull(clamped);
      // Must preventDefault to stop iOS from handling this as a native scroll/bounce
      e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      const currentPull = pullRef.current;
      pullRef.current = 0;
      setPull(0);
      if (currentPull >= THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        try { await onRefreshRef.current(); } finally {
          refreshingRef.current = false;
          setRefreshing(false);
        }
      }
    };

    // passive: false is required on touchmove so we can call preventDefault()
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, []); // empty — all mutable state accessed via refs

  const progress = Math.min(pull / THRESHOLD, 1);

  return (
    <Box ref={scrollRef} sx={{ position: "relative", ...sx }}>
      {/* Pull indicator */}
      <Box sx={{
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: refreshing ? 1 : progress,
        transform: `translateY(${refreshing ? pull : pull - 48}px)`,
        transition: pull === 0 && !refreshing ? "opacity 0.2s, transform 0.2s" : "none",
        pointerEvents: "none",
        zIndex: 10,
      }}>
        {refreshing
          ? <CircularProgress size={22} thickness={4} />
          : <RefreshIcon sx={{ color: "text.secondary", transform: `rotate(${progress * 240}deg)` }} />
        }
      </Box>

      {/* Content slides down slightly during pull */}
      <Box sx={{
        transform: `translateY(${pull > 0 || refreshing ? Math.min(pull, 48) : 0}px)`,
        transition: pull === 0 && !refreshing ? "transform 0.2s" : "none",
      }}>
        {children}
      </Box>
    </Box>
  );
}
